// @flow

import {Vector2D} from './Vector2D/vector2d'
import type {vector} from './Vector2D/vector2d'
import type {Enkeltmandat} from '../../../domain/result'

export type Pose = {
  position: vector,
  direction: vector
}

export type Move = {
  type: 'forward' | 'two forward' | 'diagonal left' | 'rotate right',
  from: Pose,
  to: Pose
}

export type Moves = {
  kortNavn: string,
  moves: Array<Move>
}

type Rule = {
  id: 'forward' | 'two forward' | 'diagonal left' | 'rotate right',
  getMove: vector => vector,
  getDirection: vector => vector
}

function getItem(matrix, position) {
  const [x, y] = position

  if (x === 21) {
    // center dot
    const yy = y >= 0 && y < 4 ? 2 : y
    return matrix[21][yy]
  }

  return (matrix[x] || [])[y]
}

function same(a, b) {
  return a && b && a.kortNavn === b.kortNavn
}

function rotateLeft(direction) {
  return Vector2D.rotate(direction, -Math.PI / 2)
}

function rotateRight(direction) {
  return Vector2D.rotate(direction, Math.PI / 2)
}

function copyVec([x, y]: vector): vector {
  // Jest considers -0 !== 0, so we must normalize to unsigned zero
  return [x === 0 ? 0 : x, y === 0 ? 0 : y]
}

const rules: Array<Rule> = [
  {
    id: 'diagonal left',
    getMove: direction => Vector2D.add(direction, rotateLeft(direction)),
    getDirection: direction => rotateLeft(direction)
  },
  {
    id: 'forward',
    getMove: direction => direction,
    getDirection: direction => direction
  },
  {
    id: 'two forward',
    getMove: direction => Vector2D.add(direction, direction),
    getDirection: direction => direction
  },
  {
    id: 'rotate right',
    getMove: () => [0, 0],
    getDirection: direction => rotateRight(direction)
  }
]

function nextMove(matrix, position, direction): Move {
  const currentItem = getItem(matrix, position)
  const rule = rules.find(rule => {
    const canditateItem = getItem(
      matrix,
      Vector2D.add(position, rule.getMove(direction))
    )
    return same(canditateItem, currentItem)
  })

  if (!rule) {
    throw Error('No default rule')
  }

  return {
    type: rule.id,
    from: {position: copyVec(position), direction: copyVec(direction)},
    to: {
      position: Vector2D.add(position, rule.getMove(direction)),
      direction: copyVec(rule.getDirection(direction))
    }
  }
}

function equals(a, b) {
  return a[0] === b[0] && a[1] === b[1]
}

export function traceItems(
  matrix: Array<Array<Enkeltmandat>>,
  position: vector,
  direction: vector
): Array<Move> {
  let count = 0
  let current = {position, direction}

  const moves = []
  while (count++ <= 60) {
    const move = nextMove(matrix, current.position, current.direction)
    moves.push(move)
    current = move.to

    if (
      equals(current.position, position) &&
      equals(current.direction, direction)
    ) {
      break
    }
  }
  return moves
}

export function traceAll(matrix: Array<Array<Enkeltmandat>>): Array<Moves> {
  return matrix.reduce((cacc, column, cIdx) => {
    return [
      ...cacc,
      ...column.reduce((racc, representant, rIdx) => {
        if (
          typeof representant === 'undefined' ||
          cacc.some(({kortNavn}) => kortNavn === representant.kortNavn) ||
          racc.some(({kortNavn}) => kortNavn === representant.kortNavn)
        ) {
          return racc
        }

        const moves = traceItems(matrix, [cIdx, rIdx], [0, -1])
        return [...racc, {kortNavn: representant.kortNavn, moves}]
      }, [])
    ]
  }, [])
}



// WEBPACK FOOTER //
// ./src/shared/components/shared/mandat-svg/tracer.js